import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FileSpreadsheet, Upload, ChevronRight, ArrowDownLeft, ArrowUpRight, Loader2, ArrowLeft,
  CheckCircle2, HelpCircle, UserPlus, Users, Landmark, AlertTriangle, RotateCcw, History, Download,
} from 'lucide-react';
import {
  PageHead, Panel, StatCard, StatRow, Pill, Select, Modal, btn, field,
} from '../common/AdminUI';
import SettleModal from './SettleModal';
import {
  readStatementFile, uploadBankRows, addArrivalsToBalances, listBankRows, assignBankRows,
  listUsers, listCreditors, listOpenInvoices, listOpenPurBills, settle, money, moneyShort, dmy,
  planRowReduction, reduceBankRows,
  undoPaymentItems, restoreBankRow, listSettledBills,
  archiveStatementFile, listBankStatements, statementDownloadUrl,
} from './reconApi';
import { BANKS, RULES_READ_FROM } from './bankStatementParser';

/**
 * Steps 3 to 5, and the door to 6 and 7.
 *
 * The statement comes in as Excel, every line's name is pulled out of its
 * remark, and the same name is shown once with its credits and debits added up.
 * Opening a row shows the dates that made those totals, because a single figure
 * is not something an accountant will settle against without seeing it broken up.
 */


const settleBtn =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-bold text-white ' +
  'whitespace-nowrap transition disabled:opacity-40 disabled:cursor-not-allowed';

const Money = ({ value, tone }) => (
  value > 0
    ? <span className={`font-semibold tabular-nums ${tone}`}>₹{money(value)}</span>
    : <span className="text-slate-300 dark:text-slate-600">—</span>
);

/* ── the rows under a group ──────────────────────────────────────────────── */

const Breakdown = ({ lines, span = 4, side = 'credit' }) => (
  <tr className="bg-slate-50/70 dark:bg-slate-900/40">
    <td colSpan={span} className="px-5 py-3.5">
      <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-2.5">
        {lines.length} line{lines.length === 1 ? '' : 's'} · {side === 'credit' ? 'credit' : 'debit'}
      </p>
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900/60">
        {lines.map((l, i) => (
          <div key={i}
            className={`flex flex-wrap items-start gap-x-4 gap-y-1 px-3.5 py-2.5 text-[12.5px]
                        ${i > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}`}>
            <span className="tabular-nums text-slate-500 dark:text-slate-400 w-[92px] flex-shrink-0">{dmy(l.date)}</span>
            <span className="text-slate-600 dark:text-slate-400 flex-1 min-w-[180px] break-all">
              {l.remark}
            </span>
            <span className="w-32 text-right">
              <Money value={side === 'credit' ? l.credit : l.debit}
                tone={side === 'credit'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'} />
            </span>
          </div>
        ))}
      </div>
    </td>
  </tr>
);

/* ── one section ─────────────────────────────────────────────────────────── */

const SECTION = {
  credit: {
    title: 'Credit', icon: ArrowDownLeft, tone: 'emerald',
    who: 'Goes to', blurb: 'Receipts matched to the customer who sent them.',
    amount: 'text-emerald-600 dark:text-emerald-400',
    button: 'bg-emerald-600 hover:bg-emerald-700',
  },
  debit: {
    title: 'Debit', icon: ArrowUpRight, tone: 'amber',
    who: 'Creditor', blurb: 'Payments matched to the creditor that billed us.',
    amount: 'text-amber-600 dark:text-amber-400',
    button: 'bg-amber-600 hover:bg-amber-700',
  },
  misc: {
    title: 'Miscellaneous', icon: HelpCircle, tone: 'rose',
    who: 'Match with', blurb: 'Money whose name matched nobody. Point it at someone and it moves to the right list.',
    amount: 'text-slate-600 dark:text-slate-300',
    button: 'bg-slate-600 hover:bg-slate-700',
  },
};

const addUp = (list) => Math.round(list.reduce((t, e) => t + e.amount, 0) * 100) / 100;

const MISC_SIDE = {
  credit: {
    title: 'Credit', icon: ArrowDownLeft, tone: 'emerald',
    who: 'Match to a customer', pick: 'Pick a customer…',
    blurb: 'money in, the sender was not recognised',
    amount: 'text-emerald-600 dark:text-emerald-400',
    chip: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  debit: {
    title: 'Debit', icon: ArrowUpRight, tone: 'amber',
    who: 'Match to a creditor', pick: 'Pick a creditor…',
    blurb: 'money out, the payee was not recognised',
    amount: 'text-amber-600 dark:text-amber-400',
    chip: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  },
};

/**
 * One direction of the unplaced money, inside the Miscellaneous panel.
 *
 * The two are kept apart here for the same reason they are apart above: money
 * in came from a customer and money out went to a creditor, so a name on one
 * side is never picked from the other side's list. Mixing them put both
 * amounts in one table and left the accountant reading which column was
 * filled to know what kind of name they were looking at.
 */
const MiscGroup = ({ side, rows, total, open, onToggle, onMatch, options, className = '' }) => {
  const look = MISC_SIDE[side];
  const Icon = look.icon;

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2.5 px-4 sm:px-5 py-2.5 bg-slate-50/80 dark:bg-slate-800/30">
        <span className={`grid place-items-center w-6 h-6 rounded-lg flex-shrink-0 ${look.chip}`}>
          <Icon size={13} />
        </span>
        <h4 className="text-[13px] font-bold text-slate-700 dark:text-slate-200">{look.title}</h4>
        <Pill tone={look.tone}>{rows.length}</Pill>
        <span className="text-[11.5px] text-slate-400 dark:text-slate-500">{look.blurb}</span>
        <p className={`ml-auto text-[14.5px] font-black tabular-nums ${look.amount}`}>₹{money(total)}</p>
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-6 text-center text-[12.5px] text-slate-400">Nothing unplaced on this side</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                {['Name and amount', look.who].map((h, i) => (
                  <th key={i}
                    className="px-4 sm:px-5 py-2.5 text-left text-[10.5px] font-black uppercase
                               tracking-[0.1em] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {rows.map((e) => {
                const isOpen = open.includes(e.id);
                return (
                  <React.Fragment key={e.id}>
                    <tr onClick={() => onToggle(e.id)}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition cursor-pointer">
                      {/* The amount sits under the name and the line count to
                          its right, so the row stays inside half a screen. */}
                      <td className="px-4 sm:px-5 py-3.5 w-full max-w-0">
                        <div className="flex items-start gap-2.5">
                          <ChevronRight size={15}
                            className={`text-slate-400 flex-shrink-0 mt-0.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <p className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate min-w-0"
                                title={e.extractedName}>{e.extractedName}</p>
                              <span className="ml-auto text-[11px] text-slate-400 flex-shrink-0 tabular-nums">
                                {e.lines.length} line{e.lines.length === 1 ? '' : 's'}
                              </span>
                            </div>
                            <p className="text-[13.5px] mt-0.5">
                              <Money value={e.amount} tone={look.amount} />
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 sm:px-5 py-3.5 w-px whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                        <div className="flex items-center gap-2.5">
                          <Select
                            value=""
                            onChange={(v) => onMatch(e, v)}
                            allLabel={look.pick}
                            icon={UserPlus}
                            className="w-[215px]"
                            options={(options[side] || [])
                              .filter((o) => !e.candidates?.length || e.candidates.some((c) => c.id === o.id))
                              .map((o) => ({ value: String(o.id), label: o.name }))}
                          />
                          {e.candidates?.length > 1 && <Pill tone="amber">{e.candidates.length} fit</Pill>}
                        </div>
                      </td>
                    </tr>
                    {isOpen && <Breakdown lines={e.lines} span={2} side={side} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** A card's headline: short enough to be read at a glance, exact on hover. */
const Big = ({ value, tone = 'text-slate-900 dark:text-white' }) => (
  <span className={`tabular-nums ${tone}`} title={`₹${money(value)}`}>₹{moneyShort(value)}</span>
);

/**
 * The unplaced money, split the way the panel below it is split.
 *
 * One figure answered "how much is waiting" but not "waiting on which side",
 * which is what decides whether a customer or a creditor has to be picked. The
 * two halves say it without opening the panel.
 */
const MiscCard = ({ credit, debit }) => {
  const half = (side, list, first) => {
    const look = MISC_SIDE[side];
    const Icon = look.icon;
    return (
      <div className={`min-w-0 ${first ? 'pr-3' : 'pl-3 border-l border-slate-200 dark:border-slate-800'}`}>
        {/* The count rides on the label, as a figure only: spelling out
            "names" wrapped the label onto a second line and took the card
            back to being taller than the three beside it. */}
        <p className="flex items-center gap-1.5 whitespace-nowrap text-[9.5px] font-black uppercase
                      tracking-[0.12em] text-slate-400 dark:text-slate-500">
          <Icon size={11} className={look.amount} /> {look.title}
          <span className="font-bold tracking-normal text-[10px]">· {list.length}</span>
        </p>
        <p className="text-[20px] leading-none font-black mt-2 truncate">
          <Big value={addUp(list)} tone={look.amount} />
        </p>
      </div>
    );
  };

  return (
    <Panel className="relative overflow-hidden px-4 sm:px-5 pt-4 pb-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
          Miscellaneous
        </p>
        <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                         bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300">
          <HelpCircle size={17} />
        </span>
      </div>

      {/* Two halves rather than one figure: which side the money is waiting on
          is what decides whether a customer or a creditor has to be picked. */}
      <div className="grid grid-cols-2 gap-0 mt-2">
        {half('credit', credit, true)}
        {half('debit', debit, false)}
      </div>
      <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-rose-500" />
    </Panel>
  );
};

/**
 * A section: the matched credit, the matched debit, or everything unplaced.
 *
 * Miscellaneous is the parent of its two directions rather than a list of its
 * own, so what is read above as Credit and Debit is read the same way here.
 */
const Section = ({ kind, rows, total, open, onToggle, onMatch, onSettle, options }) => {
  const look = SECTION[kind];
  const Icon = look.icon;
  const misc = kind === 'misc';

  return (
    <Panel className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-800">
        <span className={`grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                          ${kind === 'credit' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300'
                            : kind === 'debit' ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300'
                            : 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300'}`}>
          <Icon size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-[15px] font-bold text-slate-900 dark:text-white">{look.title}</h3>
            <Pill tone={look.tone}>{rows.length}</Pill>
          </div>
          <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">{look.blurb}</p>
        </div>
        <p className={`text-[17px] font-black tabular-nums ${look.amount}`}>₹{money(total)}</p>
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-[13px] text-slate-400">Nothing here</p>
      ) : misc ? (
        /* Side by side, the way the matched two are read above; they stack on
           a narrow screen, where the divider becomes a line between them. */
        <div className="grid grid-cols-1 xl:grid-cols-2">
          {['credit', 'debit'].map((side) => {
            const own = rows.filter((e) => e.side === side);
            return (
              <MiscGroup key={side} side={side} rows={own} total={addUp(own)}
                className={side === 'credit'
                  ? 'border-slate-200 dark:border-slate-800 xl:border-r'
                  : 'border-slate-200 dark:border-slate-800 border-t xl:border-t-0'}
                open={open} onToggle={onToggle} onMatch={onMatch} options={options} />
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                {['Name in the remark', 'Amount', ''].map((h, i) => (
                  <th key={i}
                    className={`px-4 sm:px-5 py-3 text-[10.5px] font-black uppercase tracking-[0.1em]
                                text-slate-400 dark:text-slate-500 whitespace-nowrap
                                ${i === 1 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {rows.map((e) => {
                const isOpen = open.includes(e.id);
                return (
                  <React.Fragment key={e.id}>
                    <tr onClick={() => onToggle(e.id)}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition cursor-pointer">
                      <td className="px-4 sm:px-5 py-3.5 w-full max-w-0">
                        <div className="flex items-center gap-2.5">
                          <ChevronRight size={15}
                            className={`text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate"
                              title={e.extractedName}>{e.extractedName}</p>
                            <p className="flex items-center gap-1.5 mt-1 min-w-0">
                              <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                              <span className="text-[11.5px] font-semibold text-slate-600 dark:text-slate-300 truncate min-w-0"
                                title={e.placed.name}>{e.placed.name}</span>
                              <span className="text-[11px] text-slate-400 flex-shrink-0">· {e.lines.length} line{e.lines.length === 1 ? '' : 's'}</span>
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 sm:px-5 py-3.5 text-right whitespace-nowrap w-px">
                        <Money value={e.amount} tone={look.amount} />
                      </td>

                      <td className="px-3 py-3.5 whitespace-nowrap w-px" onClick={(ev) => ev.stopPropagation()}>
                        <button type="button" onClick={() => onSettle(e)}
                          className={`${settleBtn} ${look.button}`}
                          title={e.side === 'credit' ? 'Put this receipt against invoices' : 'Put this payment against the creditor’s bills'}>
                          {e.side === 'credit' ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />} Settle
                        </button>
                      </td>
                    </tr>
                    {isOpen && <Breakdown lines={e.lines} span={3} side={e.side} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

/* ── the screen ──────────────────────────────────────────────────────────── */

const BankStatement = ({ search = '', onBack, onSettled }) => {
  const [statement, setStatement] = useState(null);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [reading, setReading] = useState(false);

  /* Which bank this statement is from, chosen before the file is picked.
     The same accountant uploads the same bank month after month, so the last
     choice is offered again; a blocked or empty store just means no default. */
  const [bank, setBank] = useState(() => {
    try { return localStorage.getItem('ql.bankStatement.bank') || ''; } catch { return ''; }
  });
  const chooseBank = (v) => {
    setBank(v);
    try { localStorage.setItem('ql.bankStatement.bank', v); } catch { /* not worth a word */ }
  };
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState([]);
  const fileRef = useRef(null);

  /* Settled money is taken off the bank line on the server, so there is
     normally nothing to remember here. This is the fallback for when that
     write fails: the screen still shows the row reduced, for this tab only. */
  const [spent, setSpent] = useState({});

  /* bank_details keeps the name we pulled out of the remark, not the remark
     itself, so the full line is held here from the read that produced it.
     It lasts as long as the tab does; storing it belongs on the server. */
  const remarks = useRef(new Map());
  const remarkKey = (r) => `${r.name}|${r.date}|${r.credit || 0}|${r.debit || 0}`;

  /* Which undo is in flight, what it would take back, and why. The question
     is asked in a window of its own over the settle screen, so what is about
     to happen is the only thing on it. */
  const [undoBusy, setUndoBusy] = useState('');
  const [undoAsk, setUndoAsk] = useState(null);
  const [reason, setReason] = useState('');

  /* The upload is a window of its own: the bank is chosen with the file it
     belongs to, rather than on a bar that asked for it long before. */
  const [uploadOpen, setUploadOpen] = useState(false);
  const [chosenFile, setChosenFile] = useState(null);

  /* The workbooks kept in storage, read only when asked for. */
  const [pastOpen, setPastOpen] = useState(false);
  const [past, setPast] = useState(null);

  const [settling, setSettling] = useState(null);
  const [bills, setBills] = useState([]);
  // what this party has already been paid, which the same screen can take back
  const [paidBills, setPaidBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try { setRows(await listBankRows()); }
    catch (err) { toast.error(err.message || 'Could not load the statement rows'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    listUsers().then(setUsers).catch(() => {});
    listCreditors().then((c) => setCreditors(c.map((x) => ({ id: x.id, name: x.name })))).catch(() => {});
    refresh();
  }, [refresh]);

  /**
   * The first statement goes straight in — the screen is empty, the bank is
   * right there and there is nothing to get in the way of. Once there are rows
   * on it, uploading again is a deliberate act, and the window asks for the
   * bank and the file together rather than firing a file dialog at them.
   */
  const askFirst = () => rows.length > 0;

  const startUpload = () => {
    if (askFirst()) { setUploadOpen(true); return; }
    if (!bank) { toast.error('Choose which bank this statement is from'); return; }
    fileRef.current?.click();
  };

  const pick = async (f) => {
    if (!f) return;
    if (!bank) { toast.error('Choose which bank this statement is from'); return; }
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) { toast.error('A bank statement comes as an Excel or CSV'); return; }
    setReading(true);
    const t = toast.loading('Reading the statement…');
    try {
      const read = await readStatementFile(f, bank);
      if (!read.rows.length) throw new Error('No transactions with a counterparty in this file');
      toast.loading(`Sending ${read.rows.length} rows…`, { id: t });
      read.rows.forEach((r) => {
        const k = remarkKey({ name: r.name, date: r.date, credit: r.credit || 0, debit: r.debit || 0 });
        const seen = remarks.current.get(k) || [];
        seen.push(r.remark);
        remarks.current.set(k, seen);
      });
      const { created, ambiguousNames, placed } = await uploadBankRows(read.rows);
      // what arrived goes onto each party's balance before the screen is drawn
      let onBalance = { users: 0, creditors: 0 };
      try { onBalance = await addArrivalsToBalances(placed); }
      catch (e) { toast.error(`Rows saved, but balances did not move — ${e.message}`, { duration: 7000 }); }
      setStatement({ fileName: read.fileName, bank: read.bank, period: read.period, holder: read.holder });

      /* The rows are the point; keeping the workbook is worth saying if it
         fails but never worth losing them over. */
      let kept = true;
      try { await archiveStatementFile(f, bank); }
      catch { kept = false; }

      await refresh();
      const parties = onBalance.users + onBalance.creditors;
      toast.success(
        `${created} rows saved${read.skipped ? ` · ${read.skipped} had no name or date` : ''}`
        + (parties ? ` · ${parties} balance${parties === 1 ? '' : 's'} updated` : '')
        + (ambiguousNames.length ? ` · ${ambiguousNames.length} need a choice` : '')
        + (kept ? '' : ' · the file itself was not kept'),
        { id: t, duration: 6000 },
      );
    } catch (err) {
      toast.error(err.message || 'Could not read this statement', { id: t });
    } finally {
      setReading(false);
      setUploadOpen(false);
      setChosenFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* One row per transaction comes back; the screen shows a party at a time.
     Rows the server placed group by that user; the rest group by the name the
     bank wrote, which is all they have in common. */
  const entries = useMemo(() => {
    // a fresh queue each build, so each line takes its own remark
    const pending = new Map();
    remarks.current.forEach((list, k) => pending.set(k, [...list]));

    const byKey = new Map();
    rows.forEach((r) => {
      // Settled in full: its amount is gone, so there is nothing left to show.
      if (!(r.credit > 0) && !(r.debit > 0)) return;
      /* Money in is a customer paying us, money out is us paying a creditor,
         and the server resolves each against its own side. */
      const side = r.credit > 0 ? 'credit' : 'debit';
      const placed = side === 'credit'
        ? (r.userId ? { id: r.userId, name: r.userName } : null)
        : (r.creditorId ? { id: r.creditorId, name: r.creditorName } : null);

      const key = `${side}:${placed ? `p${placed.id}` : `n${r.name}`}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          id: key, side, extractedName: r.name, placed, candidates: r.candidates,
          amount: 0, ids: [], lines: [],
        });
      }
      const e = byKey.get(key);
      if (r.name.length > e.extractedName.length) e.extractedName = r.name;
      e.amount = Math.round((e.amount + (side === 'credit' ? r.credit : r.debit)) * 100) / 100;
      e.ids.push(r.id);
      const queue = pending.get(remarkKey(r));
      e.lines.push({
        id: r.id,
        date: r.date,
        remark: (queue && queue.shift()) || r.name,
        credit: r.credit,
        debit: r.debit,
      });
    });

    return [...byKey.values()]
      .map((e) => ({
        ...e,
        // the row carries what is left of it, not what arrived
        amount: Math.round((e.amount - (spent[e.id] || 0)) * 100) / 100,
        received: e.amount,
        lines: e.lines.sort((a, b) => a.date.localeCompare(b.date)),
      }))
      .filter((e) => e.amount > 0.009)
      .sort((a, b) => b.amount - a.amount);
  }, [rows, spent]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => (
      e.extractedName.toLowerCase().includes(q)
      || String(e.placed?.name || '').toLowerCase().includes(q)
    ));
  }, [entries, search]);

  const lists = useMemo(() => ({
    credit: shown.filter((e) => e.side === 'credit' && e.placed),
    debit: shown.filter((e) => e.side === 'debit' && e.placed),
    misc: shown.filter((e) => !e.placed),
  }), [shown]);

  const sum = (list) => Math.round(list.reduce((s, e) => s + e.amount, 0) * 100) / 100;

  const toggle = (id) => setOpen((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  /**
   * Point every one of this party's rows at somebody.
   *
   * The server is told either way: a customer on the credit side, a creditor
   * on the debit side. It clears the flag and learns that this is one of the
   * names that party's bank writes, so the next statement places it alone.
   */
  const doMatch = async (entry, pickedId) => {
    if (!pickedId) return;
    const from = entry.side === 'debit' ? creditors : users;
    const hit = from.find((x) => String(x.id) === String(pickedId));
    if (!hit) return;

    try {
      await assignBankRows(entry.ids, entry.side, hit.id);
      toast.success(`Assigned to ${hit.name} · the name is remembered`);
      await refresh();
    } catch (err) { toast.error(err.message || 'Could not assign this row'); }
  };

  const openSettle = async (entry) => {
    setSettling(entry);
    setBillsLoading(true);
    setBills([]);
    setPaidBills([]);
    try {
      const [open, already] = await Promise.all([
        entry.side === 'credit' ? listOpenInvoices(entry.placed.id) : listOpenPurBills(entry.placed.id),
        listSettledBills({ side: entry.side, partyId: entry.placed.id }),
      ]);
      setBills(open);
      setPaidBills(already);
    } catch { toast.error('Could not load what this can settle against'); }
    finally { setBillsLoading(false); }
  };

  const reloadBills = async () => {
    if (!settling?.placed?.id) return;
    try {
      const [open, already] = await Promise.all([
        settling.side === 'credit' ? listOpenInvoices(settling.placed.id) : listOpenPurBills(settling.placed.id),
        listSettledBills({ side: settling.side, partyId: settling.placed.id }),
      ]);
      setBills(open);
      setPaidBills(already);
    } catch { /* what is on screen stays */ }
  };

  /**
   * Take payments back, from the screen that made them.
   *
   * The server returns the money to the balance, takes it off each bill and
   * works its status out again, in one all-or-nothing call. The bank lines are
   * ours: `reduceBankRows` took this money off them, so it goes back on, a line
   * at a time since one batch can span several.
   */
  const undoMany = async (items, why) => {
    if (!items.length) return;
    setUndoBusy('modal');
    try {
      await undoPaymentItems(items.map((i) => i.id), why);

      /* Where the money goes back to.
         A payment names the line it came off, but that line is often not there
         any more — it was settled before payments were recorded, or its sheet
         has since been replaced. The party's balance has gone back up on the
         server either way, and a line here carries what is left of it rather
         than what the bank printed, so the amount lands on the earliest line
         this party still has. Dropping it, as this did, left the screen
         saying they had no more money than before the undo. */
      const here = new Set(rows.map((r) => r.id));
      const fallback = (live || settling).lines.find((l) => here.has(l.id))?.id || null;
      let moved = false;

      const byRow = new Map();
      items.forEach((i) => {
        const target = i.bankRowId && here.has(i.bankRowId) ? i.bankRowId : fallback;
        if (!target) return;
        if (target !== i.bankRowId) moved = true;
        byRow.set(target, Math.round(((byRow.get(target) || 0) + i.amount) * 100) / 100);
      });
      let backOnRow = byRow.size === 0 ? null : true;
      for (const [rowId, amount] of byRow) {
        try { await restoreBankRow(rowId, settling.side, amount); }
        catch { backOnRow = false; }
      }

      setUndoAsk(null);
      setReason('');
      await Promise.all([refresh(), reloadBills()]);
      onSettled?.(settling.side);
      const total = Math.round(items.reduce((sum, i) => sum + i.amount, 0) * 100) / 100;
      toast.success(
        `${items.length} ${settling.side === 'credit' ? 'invoice' : 'bill'}${items.length === 1 ? '' : 's'} due again`
        + ` · ₹${money(total)} back on their balance`
        + (backOnRow === false
          ? ' · the row still reads short'
          : backOnRow
            ? (moved ? ' · and on this row, since the line it came off is gone' : ' and on this row')
            : ' · but there is no line of theirs left to put it on'),
        { duration: 5500 },
      );
    } catch (err) {
      toast.error(err.message || 'Could not undo this', { duration: 7000 });
    } finally { setUndoBusy(''); }
  };

  const doSettle = async (allocations) => {
    setSaving(true);
    try {
      /* Which lines this money comes off is worked out first: the earliest of
         them is the one the payment record is filed against. */
      const applied = Math.round(allocations.reduce((t, a) => t + a.amount, 0) * 100) / 100;
      const plan = planRowReduction((live || settling).lines, settling.side, applied);

      const res = await settle({
        side: settling.side,
        allocations,
        received: live?.amount ?? settling.amount,
        partyId: settling.placed?.id,
        bankRowId: plan[0]?.id,
      });
      const n = allocations.length;

      /* Take the money off the lines it came from, so the row carries what is
         left of it the next time this screen is opened and not just now. */
      let offTheRow = false;
      try {
        await reduceBankRows(plan);
        offTheRow = true;
        await refresh();
      } catch {
        // the server kept the full amount; at least show it reduced here
        const id = settling.id;
        setSpent((sp) => ({ ...sp, [id]: Math.round(((sp[id] || 0) + res.applied) * 100) / 100 }));
      }

      const left = Math.round(((live?.amount ?? settling.amount) - res.applied) * 100) / 100;
      toast.success(
        `₹${money(res.applied)} against ${n} ${settling.side === 'credit' ? 'invoice' : 'bill'}${n === 1 ? '' : 's'}`
        + (left > 0.009 ? ` · ₹${money(left)} still on this row` : ' · nothing left on this row')
        + (res.balanceMoved ? '' : ' · their balance did not move')
        + (offTheRow ? '' : ' · the row keeps its full amount after a reload')
        + (res.amountRecorded ? '' : ' · a PUR records the status, not the amount'),
        { duration: res.amountRecorded ? 4500 : 6000 },
      );
      setSettling(null);
      /* The bill this money cleared now reads differently on the shipment
         table. Say so, rather than leaving the accountant to reload the page
         to see a status they have just changed themselves. */
      onSettled?.(settling.side);
    } catch (err) { toast.error(err.message || 'Could not settle this', { duration: 7000 }); }
    finally { setSaving(false); }
  };

  /* An undo puts money back on the row while this modal is open, so it reads
     the live entry rather than the one it was opened with. */
  const live = settling ? entries.find((e) => e.id === settling.id) || settling : null;

  return (
    <div>
      <PageHead
        eyebrow="Accounts · Reconciliation"
        title="Bank Statement"
        badge={rows.length ? <Pill tone="blue">{rows.length} lines</Pill> : null}
        subtitle="Upload the statement, see each name once with its credits and debits, then put that money against what it clears."
        actions={(
          /* One row that will not shrink: left to themselves these three are
             flex items the head is free to squeeze, and it squeezed the upload
             button onto a line of its own. The title beside them has room to
             give; they do not. */
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {onBack && (
              <button type="button" className={btn.quiet} onClick={onBack}>
                <ArrowLeft size={15} /> Back to Air Export
              </button>
            )}
            {/* Square, so its own classes rather than btn.quiet with the padding
                turned off: both set px, and which one wins is decided by the
                stylesheet's order, not this attribute's — the icon came out
                six pixels wide. */}
            <button type="button" title="Past statements"
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl transition
                         border border-slate-200 bg-white text-slate-600 hover:bg-slate-50
                         dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
              onClick={async () => {
                setPastOpen(true);
                setPast(null);
                try { setPast(await listBankStatements()); } catch { setPast([]); }
              }}>
              <History size={16} />
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (uploadOpen) setChosenFile(f); else pick(f);
              }} />
            <button type="button" className={btn.primary} onClick={startUpload} disabled={reading}>
              {/* "Bank statement" is the title two lines above; spelling it out
                  again here put the button on a line of its own. */}
              {reading ? <><Loader2 size={16} className="animate-spin" /> Reading…</> : <><Upload size={16} /> Upload statement</>}
            </button>
          </div>
        )}
      />

      {loading ? (
        <Panel className="px-6 py-16 text-center text-sm text-slate-400">Loading…</Panel>
      ) : rows.length === 0 ? (
        <Panel className="px-6 py-16 text-center">
          <span className="grid place-items-center w-14 h-14 rounded-2xl mx-auto
                           bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
            <FileSpreadsheet size={26} />
          </span>
          <h3 className="mt-4 text-[16px] font-bold text-slate-900 dark:text-white">No statement loaded</h3>
          <p className="mt-1.5 text-[13px] text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            The file needs a date, a remark, and the amount in a debit or a credit column. The name is
            read out of the remark.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Select
              value={bank}
              onChange={chooseBank}
              allLabel="Which bank?"
              icon={Landmark}
              className="w-[210px]"
              options={BANKS}
            />
            <button type="button" className={btn.primary} onClick={startUpload} disabled={reading || !bank}>
              {reading
                ? <><Loader2 size={16} className="animate-spin" /> Reading…</>
                : <><Upload size={16} /> Choose the file</>}
            </button>
          </div>
          {bank && bank !== RULES_READ_FROM && (
            <p className="mt-3 text-[12px] text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              The remark rules were read off {RULES_READ_FROM} statements — whatever a {bank} remark writes
              differently lands in Miscellaneous, to be pointed at a party by hand.
            </p>
          )}
        </Panel>
      ) : (
        <>
          <StatRow>
            <StatCard label="Statement" value={rows.length} icon={Users} tone="blue"
              note={statement
                ? [statement.bank, statement.fileName, statement.holder, statement.period].filter(Boolean).join(' · ')
                : 'everything saved so far'} />
            <StatCard label="Credit" value={<Big value={sum(lists.credit)} />} icon={ArrowDownLeft} tone="emerald"
              note={`${lists.credit.length} customer${lists.credit.length === 1 ? '' : 's'} · ₹${money(sum(lists.credit))}`} />
            <StatCard label="Debit" value={<Big value={sum(lists.debit)} />} icon={ArrowUpRight} tone="amber"
              note={`${lists.debit.length} creditor${lists.debit.length === 1 ? '' : 's'} · ₹${money(sum(lists.debit))}`} />
            <MiscCard
              credit={lists.misc.filter((e) => e.side === 'credit')}
              debit={lists.misc.filter((e) => e.side === 'debit')} />
          </StatRow>

          {(() => {
            const common = {
              open, onToggle: toggle, onMatch: doMatch, onSettle: openSettle,
              options: { credit: users, debit: creditors },
            };
            const section = (kind) => (
              <Section kind={kind} rows={lists[kind]} total={sum(lists[kind])} {...common} />
            );
            return (
              <>
                {/* The two directions read side by side; they stack on a narrow screen. */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5 items-start">
                  {section('credit')}
                  {section('debit')}
                </div>
                {/* Whatever is still unplaced gets the full width — it is the one
                    that needs room, because every row carries a picker. */}
                {section('misc')}
              </>
            );
          })()}

        </>
      )}

      <SettleModal
        open={Boolean(settling)}
        onClose={() => setSettling(null)}
        side={settling?.side}
        amount={live?.amount || 0}
        userName={settling?.placed?.name}
        bills={bills}
        paid={paidBills}
        loading={billsLoading}
        busy={saving}
        onSubmit={doSettle}
        onUndo={(items) => { setReason(''); setUndoAsk(items); }}
        undoing={undoBusy === 'modal'}
      />

      {/* The question stands over the settle screen rather than inside it, so
          what is about to happen is the only thing being read. */}
      <Modal
        open={Boolean(undoAsk)}
        onClose={undoBusy ? () => {} : () => setUndoAsk(null)}
        icon={RotateCcw}
        tone="rose"
        title="Undo this payment?"
        subtitle={settling?.placed?.name}
        width="max-w-lg"
        footer={(
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-[12px] text-slate-500 dark:text-slate-400 flex-1">
              This cannot be half done — all of them go back together.
            </p>
            <div className="flex gap-2.5">
              <button type="button" onClick={() => setUndoAsk(null)} disabled={Boolean(undoBusy)}
                className={`${btn.quiet} disabled:opacity-50`}>
                Go back
              </button>
              <button
                type="button"
                onClick={() => undoMany(undoAsk, reason.trim())}
                disabled={Boolean(undoBusy) || reason.trim().length === 0}
                className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-bold
                           text-white transition disabled:opacity-40 disabled:cursor-not-allowed
                           bg-rose-600 hover:bg-rose-700"
              >
                <RotateCcw size={15} /> {undoBusy ? 'Undoing…' : 'Yes, undo it'}
              </button>
            </div>
          </div>
        )}
      >
        {undoAsk && (
          <>
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 dark:border-rose-500/30
                            bg-rose-50/70 dark:bg-rose-500/[0.07] p-4">
              <AlertTriangle size={18} className="text-rose-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[14px] font-bold text-slate-900 dark:text-white">
                  Take back ₹{money(undoAsk.reduce((sum, i) => sum + i.amount, 0))} from {undoAsk.length}
                  {' '}{settling?.side === 'credit' ? 'invoice' : 'bill'}{undoAsk.length === 1 ? '' : 's'}?
                </p>
                <p className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-1">
                  The money goes back on this row and on {settling?.placed?.name || 'their'} balance, and each one
                  below is due again. Nothing is deleted — the payment keeps the reason you give.
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              {undoAsk.map((i, n) => (
                <div key={i.id}
                  className={`flex items-center gap-3 px-4 py-2.5 text-[12.5px]
                              ${n > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}`}>
                  <span className="font-mono font-semibold text-slate-900 dark:text-white truncate">{i.number}</span>
                  <span className="text-[11.5px] text-slate-400 whitespace-nowrap">payment #{i.paymentId}</span>
                  <span className="ml-auto font-bold tabular-nums text-slate-900 dark:text-white">₹{money(i.amount)}</span>
                </div>
              ))}
            </div>

            <label className="block mt-4">
              <span className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                Why is this coming back?
              </span>
              <input
                className={`${field} mt-1.5`}
                value={reason}
                maxLength={255}
                autoFocus
                placeholder="paid against the wrong invoice"
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          </>
        )}
      </Modal>

      {/* Bank first, then the file: the rules that read a remark are the
          bank's, so the one is no use without the other. */}
      <Modal
        open={uploadOpen}
        onClose={reading ? () => {} : () => setUploadOpen(false)}
        icon={Upload}
        tone="blue"
        title="Upload a bank statement"
        subtitle="An Excel export with a date, a remark, and a debit or credit column"
        width="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <button type="button" onClick={() => setUploadOpen(false)} disabled={reading}
              className={`${btn.quiet} disabled:opacity-50`}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => pick(chosenFile)}
              disabled={reading || !bank || !chosenFile}
              className={`${btn.primary} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {reading
                ? <><Loader2 size={16} className="animate-spin" /> Reading…</>
                : <><Upload size={16} /> Read and upload</>}
            </button>
          </div>
        )}
      >
        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            Which bank is it from?
          </span>
          <Select
            value={bank}
            onChange={chooseBank}
            allLabel="Pick a bank…"
            icon={Landmark}
            className="w-full mt-1.5"
            options={BANKS}
          />
        </label>

        {bank && bank !== RULES_READ_FROM && (
          /* Said plainly rather than discovered later from a Miscellaneous
             list full of names that should have been placed. */
          <p className="flex items-start gap-2 mt-2.5 text-[12.5px] text-slate-500 dark:text-slate-400">
            <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <span>
              The remark rules were read off {RULES_READ_FROM} statements. A {bank} statement still uploads —
              whatever its remarks write differently lands in Miscellaneous, to be pointed at a party by hand.
            </span>
          </p>
        )}

        <div className="mt-5">
          <span className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            The file
          </span>
          {chosenFile ? (
            <div className="mt-1.5 flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-3">
              <FileSpreadsheet size={18} className="text-emerald-500 flex-shrink-0" />
              <span className="text-[13px] font-semibold text-slate-900 dark:text-white truncate"
                title={chosenFile.name}>{chosenFile.name}</span>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={reading}
                className="ml-auto text-[12px] font-bold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
                Change
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()}
              className="mt-1.5 w-full rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700
                         px-4 py-6 text-center transition hover:border-blue-400 hover:bg-blue-50/40
                         dark:hover:border-blue-500/50 dark:hover:bg-blue-500/[0.06]">
              <FileSpreadsheet size={22} className="mx-auto text-slate-300 dark:text-slate-600" />
              <span className="block mt-2 text-[13px] font-bold text-slate-700 dark:text-slate-200">
                Choose an Excel file
              </span>
              <span className="block text-[11.5px] text-slate-400 mt-0.5">.xlsx, .xls or .csv</span>
            </button>
          )}
        </div>
      </Modal>

      <Modal
        open={pastOpen}
        onClose={() => setPastOpen(false)}
        icon={History}
        tone="blue"
        title="Past statements"
        subtitle="Every workbook that has been uploaded, newest first"
        width="max-w-xl"
      >
        {past === null ? (
          <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
        ) : past.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Nothing has been uploaded yet.</p>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {past.map((p, i) => (
              <div key={p.id}
                className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3
                            ${i > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}`}>
                <FileSpreadsheet size={15} className="text-slate-400 flex-shrink-0" />
                <span className="text-[13px] font-semibold text-slate-900 dark:text-white truncate max-w-[240px]"
                  title={p.fileName}>{p.fileName || `#${p.id}`}</span>
                <Pill tone="blue">{p.bank}</Pill>
                <span className="text-[11.5px] text-slate-400 tabular-nums">{dmy(p.at)}</span>
                <button type="button" className="ml-auto text-[12px] font-bold text-blue-600 dark:text-blue-400
                                                 hover:underline inline-flex items-center gap-1.5"
                  onClick={async () => {
                    const t = toast.loading('Getting the file…');
                    try {
                      const url = await statementDownloadUrl(p.id);
                      if (!url) throw new Error('No link came back');
                      window.open(url, '_blank', 'noopener');
                      toast.dismiss(t);
                    } catch (err) { toast.error(err.message || 'Could not fetch it', { id: t }); }
                  }}>
                  <Download size={13} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BankStatement;
